import {Routes, Route, useNavigate} from 'react-router-dom'

import { ThemeProvider, createTheme } from '@mui/material/styles'
import { PopupProvider } from '@contexts/popupContext'

import { isMobile } from 'react-device-detect'
import { Application } from "./core/Application"
import { HTTPClient } from './core/HTTPClient'
import { CoreDependencies } from './core/CoreDependencies'

// Import the ButtonShowcase component
import { MainShowcaseScreen } from '@screens/showcases/MainShowcaseScreen'
import { StartDemoScreen } from '@screens/demo/StartDemoScreen'
import { ValidateNumberScreen } from '@screens/demo/ValidateNumberScreen'
import { LocationScreen } from '@screens/demo/LocationScreen'
import { QRCodeScreen } from "@screens/demo/QRCodeScreen"
import { CheckinsScreen } from '@screens/demo/CheckinsScreen'
import { MobileStartDemoScreen } from '@screens/mobile-demo/MobileStartDemoScreen'
import { MobileValidateNumberScreen } from '@screens/mobile-demo/MobileValidateNumberScreen'
import { MobileLocationScreen } from '@screens/mobile-demo/MobileLocationScreen'
import { MobileQRCodeScreen } from '@screens/mobile-demo/MobileQRCodeScreen'
import { MobileCheckinsScreen } from '@screens/mobile-demo/MobileCheckinsScreen'
import { TokenServices } from '@services/TokenServices'
import { Logger } from '@utils/Logger'
import { useEffect } from 'react'
import { ToastProvider, useToast } from '@components/Messages/ToastProvider'
import { ToastManager } from '@components/Messages/ToastManager'

const theme = createTheme({
  typography: {
    fontFamily: 'Inter',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Light.ttf) format('truetype');
          font-style: normal;
          font-weight: 300;
        }
        
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Regular.ttf) format('truetype');
          font-style: normal;
          font-weight: 400;
        }
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-Bold.ttf) format('truetype');
          font-style: normal;
          font-weight: 700;
        }
        
        @font-face {
          font-family: 'Inter';
          src: url(../assets/fonts/inter/Inter-ExtraBold.ttf) format('truetype');
          font-style: normal;
          font-weight: 800;
        }
        
        @font-face {
          font-family: 'Readex Pro';
          src: url(../assets/fonts/readexpro/ReadexPro-Medium.ttf) format('truetype');
          font-style: normal;
          font-weight: 500;
        }
      `,
    },
  },
})

Logger.log("Start", "initializing core dependencies")
const authenticatedHTTPClient = new HTTPClient()
authenticatedHTTPClient.retryCallback = async (request, status, attemptNumber: number):Promise<boolean> => {
    Logger.log("refresh callback", "called with "+status)
    if (status != 401) {
        return false
    }
    let currentToken = Application.shared().token
    
    Logger.log("refresh callback", "current token now "+currentToken)
    if (!currentToken || currentToken.length == 0) {
        Application.shared().handleSessionExpired()
        return false 
    }

    let service = new TokenServices()

    try {
        Logger.log("refresh callback", "refreshing token")
        let newToken = await service.refreshToken(
            Application.shared().token
        )

        Application.shared().setToken(newToken)
        return true
    } catch (error) {
        Logger.logError("TokenRefresh", error.message)
        Application.shared().handleSessionExpired()
        return false
    }
}

const normalHTTPClient = new HTTPClient() // unauthenticated http client
let coreDependencies = new CoreDependencies(
    authenticatedHTTPClient,
    normalHTTPClient,
)

Application.setDependencies(coreDependencies)

const AppContent = () => {
    const navigate = useNavigate()
    const { showToast } = useToast()

    useEffect(() => {
        Application.shared().setOnSessionExpired(() => {
            navigate('/')
            ToastManager.shared().showErrorMessage(
                "Tu sesión ha expirado y debemos iniciar de nuevo la demo".localized()
            )
        })

        ToastManager.shared().setCallback((message, severity) => {
            showToast(message, severity as any)
        })
        
    }, [navigate, showToast])


    return (
        <ThemeProvider theme={theme}>
            <PopupProvider>
                <Routes>
                    {/* Component Showcase Route - accessible at /components */}
                    <Route path='/' element={
                        isMobile ? <MobileStartDemoScreen/> : <StartDemoScreen/>
                    } /> 
                    <Route path='/validate-number' element={
                        isMobile ? <MobileValidateNumberScreen/> : <ValidateNumberScreen/>
                    } /> 
                    <Route path='/location' element={
                        isMobile ? <MobileLocationScreen/> : <LocationScreen/>
                    } /> 
                    <Route path='/scan-qr-code' element={
                        isMobile ? <MobileQRCodeScreen/> : <QRCodeScreen/>
                    } /> 
                    <Route path='/checkins' element={
                        isMobile ? <MobileCheckinsScreen/> : <CheckinsScreen/>
                    } /> 


                    <Route path='/showcase' element={
                        <MainShowcaseScreen/>
                    } /> 
                    
                    {/* Default route with authentication */}
                    {/* <Route path='*' element={
                        <ProtectedAuthRoute>
                            {isMobile ? <MobileDashboard /> : <Dashboard />}
                        </ProtectedAuthRoute>
                    } />  */}
                </Routes>
            </PopupProvider>
        </ThemeProvider>
    )
}

const App = () => {
    return (
        <ToastProvider>
            <AppContent />
        </ToastProvider>
    )
}

export default App